import React from "react";
import { useTranslation } from "react-i18next";
import { config } from "~/config";
import { useAuth } from "~/features/authn/context";
import { buildTenantHost, defaultTenantShortCode, parseCotenHostname } from "~/features/tenant/utils";

const redirectAttemptKey = "dewey:tenant-redirect-attempts";
// Maximum number of redirect attempts to avoid infinite loops (typically 2 real redirects + 1 buffer)
const maxRedirectAttempts = 3;

type TenantContextValue = {
  tenantShortCode: string;
  tenantId: string | undefined;
};

const TenantContext = React.createContext<TenantContextValue>({
  tenantShortCode: defaultTenantShortCode,
  tenantId: config.rolloTenantId,
});

export const useTenant = () => React.useContext(TenantContext);

type TenantProviderProps = {
  children: React.ReactNode;
};

export const TenantProvider = ({ children }: TenantProviderProps) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const parsedHost = React.useMemo(() => {
    // Keep it simple: parse once from current hostname.
    return parseCotenHostname(window.location.hostname);
  }, []);

  const resolvedTenantState = React.useMemo(() => {
    // Hostname drives the initial tenant intent:
    // - `<tenant>.dewey.coten.*` -> `<tenant>`
    // - `dewey.coten.*` -> default tenant (`rollo`)
    const tenantSlug = parsedHost.tenantSlug;
    const hostTenantShortCode = tenantSlug.length > 0 ? tenantSlug : defaultTenantShortCode;
    const canBuildHost = Boolean(parsedHost.app && parsedHost.tld);
    const accessibleTenants = user?.tenants ?? [];

    // If the host tenant is not accessible for the user, choose a safe fallback:
    // prefer `rollo`, otherwise first accessible tenant.
    let desiredTenantShortCode = hostTenantShortCode;
    if (accessibleTenants.length > 0 && !accessibleTenants.some((t) => t.shortCode === hostTenantShortCode)) {
      const defaultTenant = accessibleTenants.find((t) => t.shortCode === defaultTenantShortCode);
      desiredTenantShortCode = defaultTenant?.shortCode ?? accessibleTenants[0].shortCode;
    }

    // Canonical host format:
    // - rollo => `dewey.coten.*` (no subdomain)
    // - others => `<tenant>.dewey.coten.*`
    const desiredHost =
      canBuildHost && parsedHost.app && parsedHost.tld
        ? buildTenantHost({
            tenantShortCode: desiredTenantShortCode,
            app: parsedHost.app,
            tld: parsedHost.tld,
          })
        : undefined;

    // Expose tenant id for data operations:
    // only inject rollo id on root host (no explicit tenant slug).
    const tenant = accessibleTenants.find((t) => t.shortCode === hostTenantShortCode);
    const tenantId = tenant?.id ?? (tenantSlug.length === 0 ? config.rolloTenantId : undefined);
    const redirectAttempts = Number(sessionStorage.getItem(redirectAttemptKey) ?? "0");

    // Prevent descendants from rendering under the wrong tenant while
    // an unresolved tenant subdomain is being redirected.
    const shouldBlockChildrenDuringRedirect =
      canBuildHost &&
      accessibleTenants.length > 0 &&
      tenantSlug.length > 0 &&
      !accessibleTenants.some((t) => t.shortCode === hostTenantShortCode) &&
      redirectAttempts < maxRedirectAttempts;

    return {
      tenantSlug,
      tenantShortCode: hostTenantShortCode,
      tenantId,
      desiredHost,
      shouldBlockChildrenDuringRedirect,
      hasAccessibleTenants: accessibleTenants.length > 0,
    };
  }, [parsedHost, user?.tenants]);

  React.useEffect(() => {
    if (!resolvedTenantState.desiredHost) {
      throw new Error(t("feature.tenant.redirectError"), {
        cause: {
          parsedHost,
          resolvedTenantState,
        },
      });
    }

    const url = new URL(window.location.href);

    if (url.hostname === resolvedTenantState.desiredHost) {
      sessionStorage.removeItem(redirectAttemptKey);
      return;
    }

    if (user && !resolvedTenantState.hasAccessibleTenants) {
      // No tenant to navigate to; avoid redirect loops.
      sessionStorage.removeItem(redirectAttemptKey);
      return;
    }

    const redirectAttempts = Number(sessionStorage.getItem(redirectAttemptKey) ?? "0");
    if (redirectAttempts >= maxRedirectAttempts) {
      // Guard against unexpected redirect loops in the browser.
      throw new Error(t("feature.tenant.redirectErrorDescription"), {
        cause: {
          redirectAttempts,
          maxRedirectAttempts,
          currentHost: window.location.hostname,
          desiredHost: resolvedTenantState.desiredHost,
        },
      });
    }

    sessionStorage.setItem(redirectAttemptKey, String(redirectAttempts + 1));
    url.hostname = resolvedTenantState.desiredHost;
    window.location.replace(url.toString());
  }, [parsedHost, resolvedTenantState, user]);

  const value = React.useMemo<TenantContextValue>(() => {
    return {
      tenantShortCode: resolvedTenantState.tenantShortCode,
      tenantId: resolvedTenantState.tenantId,
    };
  }, [resolvedTenantState]);

  return (
    <TenantContext.Provider value={value}>
      {resolvedTenantState.shouldBlockChildrenDuringRedirect ? null : children}
    </TenantContext.Provider>
  );
};
